import React, { useEffect, useState, useCallback } from "react";
import {
  fetchMyServices,
  createService,
  updateService,
  deleteService,
  fetchServiceBookings,
} from "./services/serviceService";
import UploadProgressModal from "./components/UploadProgressModal";
import { exportBookingsToCSV } from "./components/admin/adminHelpers";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Modal,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Add, Edit, Delete, Call, ExpandLess, ExpandMore } from "@mui/icons-material";

import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";

import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const ModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: 500,
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const PreviewContainer = styled(Box)({
  position: "relative",
  display: "inline-block",
  marginRight: "8px",
  marginBottom: "8px",
});

const ImagePreview = styled("img")({
  width: "70px",
  height: "70px",
  borderRadius: "8px",
  objectFit: "cover",
});

const VideoPreview = styled("video")({
  width: "90px",
  height: "70px",
  borderRadius: "8px",
  objectFit: "cover",
});

const RemoveButton = styled(IconButton)({
  position: "absolute",
  top: "-6px",
  right: "-6px",
  backgroundColor: "rgba(0,0,0,0.6)",
  color: "white",
  width: "20px",
  height: "20px",
  padding: 0,
  "&:hover": {
    backgroundColor: "rgba(255,0,0,0.7)",
  },
});

const SERVICE_TYPES = [
  "Audio visual",
  "Catering",
  "Décor",
  "Lighting",
  "Mobile toilets",
  "Photography",
  "Rentals",
  "Sound system",
  "Security",
  "Transport",
  "Videography",
];

const Services = ({ token }) => {
  const [services, setServices] = useState([]);
  const [bookingsByService, setBookingsByService] = useState([]);
  const [showBookings, setShowBookings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [search, setSearch] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });

  const [formData, setFormData] = useState({
    serviceType: "",
    name: "",
    description: "",
    country: "",
    city: "",
    charges: "",
    bookingStatus: "open",
    duration: "",
    images: [],
    videos: [],
    lat: null,
    lng: null,
  });

  const loadMyServices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMyServices(token);
      setServices(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error fetching services", severity: "error" });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadMyServices();
  }, [loadMyServices]);

  // Fetch bookings for all services
  const loadBookings = async (servicesList) => {
    const results = {};
    setLoading(true);

    try {
      for (const service of servicesList) {
        const serviceBookings = await fetchServiceBookings(service._id);
        results[service._id] = serviceBookings
          .filter((b) => b.paymentStatus === "Success")
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      }

      setBookingsByService(results);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error fetching bookings",
        severity: "error",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBookings = async () => {
    const next = !showBookings;
    setShowBookings(next);

    if (next) {
      await loadBookings(services);
    }
  };

  const handleOpenModal = (service = null) => {
    setSelectedService(service);

    if (service) {
      setFormData({
        serviceType: service.serviceType,
        name: service.name,
        description: service.description,
        country: service.country,
        city: service.city,
        charges: service.charges,
        bookingStatus: service.bookingStatus,
        duration: service.duration,
        images: service.images || [],
        videos: service.videos || [],
        lat: service.location?.coordinates?.[1] || null,
        lng: service.location?.coordinates?.[0] || null,
      });
    } else {
      setFormData({
        serviceType: "",
        name: "",
        description: "",
        country: "",
        city: "",
        charges: "",
        bookingStatus: "open",
        duration: "",
        images: [],
        videos: [],
        lat: null,
        lng: null,
      });
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    if (uploading) return;
    setOpenModal(false);
    setSelectedService(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /* -----------------------------
      IMAGE UPLOAD
  ------------------------------ */
  const handleImageUpload = (e) => {
    const selectedFiles = Array.from(e.target.files || []).slice(0, 5);

    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...selectedFiles].slice(0, 5),
    }));

    e.target.value = "";
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  /* -----------------------------
      VIDEO UPLOAD
  ------------------------------ */
  const handleVideoUpload = (e) => {
    const selectedFiles = Array.from(e.target.files || []).slice(0, 2);

    setFormData((prev) => ({
      ...prev,
      videos: [...prev.videos, ...selectedFiles].slice(0, 2),
    }));

    e.target.value = "";
  };

  const removeVideo = (index) => {
    setFormData((prev) => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index),
    }));
  };

  /* -----------------------------
      SUBMIT CREATE / UPDATE
  ------------------------------ */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const submission = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "images") {
        formData.images.forEach((img) => submission.append("images", img));
      } else if (key === "videos") {
        formData.videos.forEach((vid) => submission.append("videos", vid));
      } else {
        submission.append(key, formData[key]);
      }
    });

    try {
      setUploading(true);
      setUploadProgress(0);

      if (selectedService) {
        await updateService(selectedService._id, submission, token, (percent) => {
          setUploadProgress(percent);
        });

        setSnackbar({
          open: true,
          message: "Service updated successfully",
          severity: "success",
        });
      } else {
        await createService(submission, token, (percent) => {
          setUploadProgress(percent);
        });

        setSnackbar({
          open: true,
          message: "Service created successfully",
          severity: "success",
        });
      }

      handleCloseModal();
      loadMyServices();
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: "Upload failed", severity: "error" });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteClick = (serviceId, serviceName) => {
    setDeleteConfirm({ open: true, id: serviceId, name: serviceName });
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteService(deleteConfirm.id);
      setSnackbar({
        open: true,
        message: "Service deleted successfully",
        severity: "success",
      });
      setDeleteConfirm({ open: false, id: null, name: '' });
      loadMyServices();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error deleting service",
        severity: "error",
      });
      console.error(error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm({ open: false, id: null, name: '' });
  };

  const filteredBookingsByService = {};
  for (const [serviceId, bookings] of Object.entries(bookingsByService)) {
    filteredBookingsByService[serviceId] = bookings.filter(
      (b) =>
        b.email?.toLowerCase().includes(search.toLowerCase()) ||
        b.phone?.toLowerCase().includes(search.toLowerCase()) ||
        b.clientName?.toLowerCase().includes(search.toLowerCase())
    );
  }

  const LocationPicker = ({ lat, lng, onChange }) => {
    const position = lat && lng ? [lat, lng] : [-1.286389, 36.817223];

    function LocationMarker() {
      useMapEvents({
        click(e) {
          onChange(e.latlng.lat, e.latlng.lng);
        },
      });

      return lat && lng ? (
        <Marker
          position={[lat, lng]}
          draggable
          eventHandlers={{
            dragend: (e) => {
              const p = e.target.getLatLng();
              onChange(p.lat, p.lng);
            },
          }}
        />
      ) : null;
    }

    return (
      <MapContainer
        center={position}
        zoom={13}
        style={{ height: 250, width: "100%", borderRadius: 12 }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <LocationMarker />
      </MapContainer>
    );
  };

  return (
    <Box p={2}>
      <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: 3, bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(18,26,45,0.78)" : "rgba(255,255,255,0.82)", border: 1, borderColor: "divider", backdropFilter: "blur(16px)" }} elevation={0}>
        <Typography variant="h5" fontWeight={800}>Make your service easy to hire</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Describe what you provide, show your work, set your rate and duration, then manage every client booking from this space.
        </Typography>
      </Paper>
      <Typography variant="h5" gutterBottom textAlign="center">
        My Services
      </Typography>

      <Box display="flex" justifyContent="space-between" mb={2}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenModal()}
        >
          Add Service
        </Button>

        <Button
          variant="outlined"
          onClick={handleToggleBookings}
          startIcon={showBookings ? <ExpandLess /> : <ExpandMore />}
        >
          {showBookings ? "Hide Bookings" : "Show Bookings"}
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : showBookings ? (
        <Box mt={4}>
          <TextField
            fullWidth
            label="Search by name, email or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ mb: 2, width: "100%", maxWidth: 400 }}
          />

          {services.map((service) => {
            const serviceBookings = filteredBookingsByService[service._id] || [];
            const totalRevenue = serviceBookings.reduce(
              (sum, b) => sum + b.totalAmount,
              0
            );

            return (
              <Accordion key={service._id} sx={{ mb: 2, borderRadius: 2, boxShadow: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {service.serviceType}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {service.name} — {service.city}, {service.country}
                    </Typography>
                  </Box>

                  <Grid container justifyContent="flex-end" direction="row" gap={3} mx={2}>
                    <Paper sx={{ p: 2 }}>
                      <Typography>Total Bookings</Typography>
                      <Typography>
                        <strong>({serviceBookings.length})</strong>
                      </Typography>
                    </Paper>

                    <Paper sx={{ p: 2 }}>
                      <Typography>Total Revenue</Typography>
                      <Typography>
                        <strong>Ksh. {totalRevenue.toFixed(2)}</strong>
                      </Typography>
                    </Paper>

                    <Button
                      variant="outlined"
                      sx={{ mb: 2 }}
                      onClick={() => exportBookingsToCSV(serviceBookings, "service-bookings.csv")}
                    >
                      Export
                    </Button>
                  </Grid>
                </AccordionSummary>

                <AccordionDetails>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Client Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Booking Date</TableCell>
                          <TableCell>From</TableCell>
                          <TableCell>To</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Total Amount</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {serviceBookings.length > 0 ? (
                          serviceBookings.map((b, i) => (
                            <TableRow key={b._id}>
                              <TableCell>{i + 1}.</TableCell>
                              <TableCell>{b.clientName}</TableCell>
                              <TableCell>{b.email}</TableCell>
                              <TableCell>{b.phone}</TableCell>
                              <TableCell>
                                {b.bookingType === "multiple"
                                  ? `${new Date(b.startDate).toLocaleDateString()} - ${new Date(
                                      b.endDate
                                    ).toLocaleDateString()}`
                                  : new Date(b.bookingDate).toLocaleDateString()}
                              </TableCell>
                              <TableCell>{b.bookingType === "multiple" ? "All day" : b.from}</TableCell>
                              <TableCell>{b.bookingType === "multiple" ? "All day" : b.to}</TableCell>
                              <TableCell>
                                {typeof b.duration === "number"
                                  ? b.bookingType === "multiple"
                                    ? `${b.duration / 24} day(s) (${b.duration} hrs)`
                                    : `${b.duration} hrs`
                                  : String(b.duration)}
                              </TableCell>
                              <TableCell>Ksh. {Number(b.totalAmount).toFixed(2)}</TableCell>
                              <TableCell>
                                <IconButton href={`tel:${b.phone}`}>
                                  <Call color="primary" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              No bookings found for this service
                            </TableCell>
                          </TableRow>
                        )}

                        {serviceBookings.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={8} align="right">
                              <strong>Total Revenue:</strong>
                            </TableCell>
                            <TableCell colSpan={2}>
                              <strong>Ksh.{totalRevenue.toFixed(2)}</strong>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {services.map((service) => {
            const images = service.images || [];
            const videos = service.videos || [];

            const hasMedia = images.length > 0 || videos.length > 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={service._id}>
                <Box sx={{ borderRadius: 3, boxShadow: 3, overflow: "hidden" }} p={2}>
                  <Box mt={2}>
                    {hasMedia ? (
                      <Carousel showThumbs={false} infiniteLoop showStatus={false} autoPlay>
                        {images.map((img, index) => (
                          <div key={`img-${index}`}>
                            <img
                              src={img.url}
                              alt={`Service-img-${index}`}
                              style={{
                                width: "100%",
                                height: "200px",
                                borderRadius: "10px",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        ))}

                        {videos.map((vid, index) => (
                          <div key={`vid-${index}`}>
                            <video
                              src={vid.url}
                              controls
                              style={{
                                width: "100%",
                                height: "200px",
                                borderRadius: "10px",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        ))}
                      </Carousel>
                    ) : (
                      <Box
                        sx={{
                          height: 200,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          bgcolor: "#f5f5f5",
                          borderRadius: 2,
                        }}
                      >
                        <Typography color="text.secondary">No media uploaded</Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6">{service.serviceType}</Typography>
                  <Typography variant="body2">Name: {service.name}</Typography>
                  <Typography variant="body2">Items: {service.description}</Typography>
                  <Typography variant="body2">
                    Location: {service.city}, {service.country}
                  </Typography>
                  <Typography variant="body2">Charges/hr: {service.charges}</Typography>
                  <Typography variant="body2">Duration: {service.duration}</Typography>
                  <Typography variant="body2">Booking Status: {service.bookingStatus}</Typography>

                  <Typography variant="caption" color="text.secondary">
                    Images: {images.length} | Videos: {videos.length}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Edit />}
                      onClick={() => handleOpenModal(service)}
                    >
                      Edit
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDeleteClick(service._id, service.name)}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
      {!showBookings && !services.length && (
        <Paper sx={{ p: 3, mt: 2, borderRadius: 3, bgcolor: "background.paper", border: 1, borderColor: "divider" }} elevation={0}>
          <Typography fontWeight={700}>No services listed yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Add your service type, business name, description, pricing, location, and media to start receiving requests.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}>Add your first service</Button>
        </Paper>
      )}

      {/* --------------------------
          MODAL FORM
      -------------------------- */}
      <Modal open={openModal} onClose={() => !uploading && handleCloseModal()}>
        <Box sx={ModalStyle}>
          <Typography variant="h6" textAlign="center" gutterBottom>
            {selectedService ? "Edit Service" : "Add Service"}
          </Typography>

          <form onSubmit={handleSubmit}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Service Type</InputLabel>
              <Select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                required
              >
                {SERVICE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Provider / Business name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Description / Items included"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="City/Town"
              name="city"
              value={formData.city}
              onChange={handleChange}
              margin="normal"
              required
            />

            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Pick service location (click or drag marker)
              </Typography>

              <LocationPicker
                lat={formData.lat}
                lng={formData.lng}
                onChange={(lat, lng) =>
                  setFormData((prev) => ({ ...prev, lat, lng }))
                }
              />

              {!formData.lat && (
                <Typography variant="caption" color="text.secondary">
                  If not set, city & country will be used as fallback
                </Typography>
              )}
            </Box>

            <TextField
              fullWidth
              label="Charges (per hour)"
              name="charges"
              type="number"
              value={formData.charges}
              onChange={handleChange}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Duration (Hours per day)"
              name="duration"
              type="number"
              value={formData.duration}
              onChange={handleChange}
              margin="normal"
              required
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                name="bookingStatus"
                value={formData.bookingStatus}
                onChange={handleChange}
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>

            {/* IMAGE UPLOAD */}
            <Button variant="contained" component="label" sx={{ mt: 2 }}>
              Upload Images (max 5)
              <input type="file" hidden multiple accept="image/*" onChange={handleImageUpload} />
            </Button>

            <Box mt={1} display="flex" flexWrap="wrap">
              {formData.images.map((img, index) => {
                let src;
                if (img instanceof File) src = URL.createObjectURL(img);
                else if (typeof img === "string") src = img;
                else if (img?.url) src = img.url;

                return (
                  <PreviewContainer key={`img-${index}`}>
                    <ImagePreview src={src} alt={`preview-img-${index}`} />
                    <RemoveButton onClick={() => removeImage(index)}>
                      <CloseIcon fontSize="small" />
                    </RemoveButton>
                  </PreviewContainer>
                );
              })}
            </Box>

            {/* VIDEO UPLOAD */}
            <Button variant="contained" component="label" sx={{ mt: 2 }}>
              Upload Videos (max 2)
              <input
                type="file"
                hidden
                multiple
                accept="video/mp4,video/quicktime,video/*"
                onChange={handleVideoUpload}
              />
            </Button>

            <Box mt={1} display="flex" flexWrap="wrap">
              {formData.videos.map((vid, index) => {
                let src;
                if (vid instanceof File) src = URL.createObjectURL(vid);
                else if (typeof vid === "string") src = vid;
                else if (vid?.url) src = vid.url;

                return (
                  <PreviewContainer key={`vid-${index}`}>
                    <VideoPreview src={src} muted controls={false} />
                    <RemoveButton onClick={() => removeVideo(index)}>
                      <CloseIcon fontSize="small" />
                    </RemoveButton>
                  </PreviewContainer>
                );
              })}
            </Box>

            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button type="submit" variant="contained" fullWidth sx={{ mt: 3 }} disabled={loading}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : selectedService ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>

              <Button
                onClick={handleCloseModal}
                color="primary"
                fullWidth
                sx={{ mt: 3 }}
              >
                Cancel
              </Button>
            </Stack>
          </form>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
      <UploadProgressModal
        open={uploading}
        progress={uploadProgress}
        text={selectedService ? "Updating Service..." : "Creating Service..."}
      />

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteConfirm.open}
        onClose={handleCancelDelete}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the service <strong>"{deleteConfirm.name}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Services;